import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  LinearProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  PauseCircle as PauseCircleIcon,
  PlayCircle as PlayCircleIcon,
  Replay as ReplayIcon,
  DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";
import { useMutation } from "convex/react";
import { useStableQuery } from "../../hooks/useStableQuery";
import { api } from "../../../../convex/_generated/api";
import { relativeTime } from "../../utils/relativeTime";

/**
 * Determine health state from last poll time.
 * Exported for testing.
 */
export function getHealthState(
  lastPollTime: number | undefined,
): "connected" | "overdue" | "disconnected" {
  if (!lastPollTime) return "disconnected";
  const elapsed = Date.now() - lastPollTime;
  if (elapsed <= 15 * 60 * 1000) return "connected";
  if (elapsed <= 30 * 60 * 1000) return "overdue";
  return "disconnected";
}

const healthColors = {
  connected: {
    chip: "success" as const,
    gradient: "linear-gradient(90deg, #00b894, #55efc4)",
    bg: "rgba(0,184,148,0.08)",
    border: "rgba(0,184,148,0.15)",
  },
  overdue: {
    chip: "warning" as const,
    gradient: "linear-gradient(90deg, #fdcb6e, #e6a800)",
    bg: "rgba(253,203,110,0.08)",
    border: "rgba(253,203,110,0.15)",
  },
  disconnected: {
    chip: "error" as const,
    gradient: "linear-gradient(90deg, #ff7675, #d63031)",
    bg: "rgba(255,118,117,0.08)",
    border: "rgba(255,118,117,0.15)",
  },
};

const healthChipLabels: Record<string, string> = {
  connected: "Connected",
  overdue: "Overdue",
  disconnected: "Disconnected",
};

const queueStatusColors: Record<string, "info" | "primary" | "success" | "error" | "default"> = {
  queued: "info",
  sent: "primary",
  completed: "success",
  failed: "error",
  expired: "default",
};

interface QBConnectionCardProps {
  isAdmin: boolean;
}

export function QBConnectionCard({ isAdmin }: QBConnectionCardProps) {
  const syncStatus = useStableQuery(api.qbDesktopQueue.getSyncStatus, {}, null);
  const queueCounts = useStableQuery(api.qbDesktopQueue.getQueueCounts, {}, null);
  const recentFailures = useStableQuery(
    api.qbDesktopQueue.listCommands,
    { status: "failed", limit: 5 },
    [],
  );

  const pauseQueue = useMutation(api.qbDesktopQueue.pauseQueue);
  const resumeQueue = useMutation(api.qbDesktopQueue.resumeQueue);
  const forceRetryAll = useMutation(api.qbDesktopQueue.forceRetryAll);
  const clearFailures = useMutation(api.qbDesktopQueue.clearFailures);

  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [expandedFailure, setExpandedFailure] = useState<string | false>(false);

  const health = getHealthState(syncStatus?.lastPollTime);
  const colors = healthColors[health];
  const isPaused = syncStatus?.isPaused ?? false;

  const handlePauseToggle = async () => {
    if (isPaused) {
      await resumeQueue();
    } else {
      await pauseQueue();
    }
  };

  const handleForceRetryAll = async () => {
    await forceRetryAll();
    setRetryDialogOpen(false);
  };

  const handleClearFailures = async () => {
    setClearing(true);
    try {
      await clearFailures();
    } finally {
      setClearing(false);
      setClearDialogOpen(false);
    }
  };

  const failedCount = queueCounts?.failed ?? 0;

  if (syncStatus === undefined) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>QuickBooks Desktop</Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card
        sx={{
          background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg.replace("0.08", "0.02")})`,
          border: `1px solid ${colors.border}`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: colors.gradient,
          },
        }}
      >
        <CardContent>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">QuickBooks Desktop</Typography>
            <Chip label={healthChipLabels[health]} color={colors.chip} size="small" />
          </Stack>

          {/* Info rows */}
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Company Name</Typography>
              <Typography variant="body2">{syncStatus?.companyName ?? "--"}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">QB Version</Typography>
              <Typography variant="body2">{syncStatus?.qbVersion ?? "--"}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Connection Status</Typography>
              <Typography variant="body2">{healthChipLabels[health]}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Last Sync</Typography>
              <Typography variant="body2">
                {syncStatus?.lastPollTime ? relativeTime(syncStatus.lastPollTime) : "Never"}
              </Typography>
            </Stack>
          </Stack>

          {/* Paused alert */}
          {isPaused && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Queue paused -- commands will not be sent to QuickBooks until resumed.
            </Alert>
          )}

          {/* Queue summary */}
          {queueCounts && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {(Object.entries(queueCounts) as [string, number][]).map(([status, count]) => (
                <Chip
                  key={status}
                  label={`${count} ${status.charAt(0).toUpperCase() + status.slice(1)}`}
                  size="small"
                  color={queueStatusColors[status] ?? "default"}
                />
              ))}
            </Stack>
          )}

          {/* Recent failures */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Recent Failures
            </Typography>
            {isAdmin && failedCount > 0 && (
              <Button
                size="small"
                variant="text"
                color="inherit"
                startIcon={<DeleteSweepIcon sx={{ fontSize: 16 }} />}
                onClick={() => setClearDialogOpen(true)}
                sx={{
                  textTransform: "none",
                  fontSize: "0.7rem",
                  color: "var(--c-text-secondary)",
                  "&:hover": { color: "var(--c-text-primary)", background: "var(--c-overlay-4)" },
                }}
              >
                Clear ({failedCount})
              </Button>
            )}
          </Stack>
          {recentFailures && recentFailures.length > 0 ? (
            recentFailures.map((cmd: any) => (
              <FailureAccordion
                key={cmd._id}
                command={cmd}
                expanded={expandedFailure === cmd._id}
                onChange={() =>
                  setExpandedFailure(expandedFailure === cmd._id ? false : cmd._id)
                }
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No recent failures
            </Typography>
          )}

          {/* Admin actions */}
          {isAdmin && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={isPaused ? <PlayCircleIcon /> : <PauseCircleIcon />}
                onClick={handlePauseToggle}
              >
                {isPaused ? "Resume Queue" : "Pause Queue"}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<ReplayIcon />}
                onClick={() => setRetryDialogOpen(true)}
              >
                Retry All Failed
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Force Retry confirmation dialog */}
      <Dialog open={retryDialogOpen} onClose={() => setRetryDialogOpen(false)}>
        <DialogTitle>Retry all failed commands?</DialogTitle>
        <DialogContent>
          <Typography>
            This will re-queue {failedCount} failed commands for processing. Commands with terminal
            errors will be skipped.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetryDialogOpen(false)} variant="outlined">
            Keep Queue
          </Button>
          <Button onClick={handleForceRetryAll} variant="contained" color="error">
            Retry All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Failures confirmation dialog */}
      <Dialog open={clearDialogOpen} onClose={() => !clearing && setClearDialogOpen(false)}>
        <DialogTitle>Clear failed commands?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            This will <b>permanently delete</b> {failedCount} failed command
            {failedCount === 1 ? "" : "s"} from the queue. The history will be removed and cannot
            be recovered.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use this only to dismiss old/stale failures. To re-attempt them instead, use
            "Retry All Failed".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearDialogOpen(false)}
            variant="outlined"
            disabled={clearing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearFailures}
            variant="contained"
            color="error"
            disabled={clearing}
            startIcon={<DeleteSweepIcon />}
          >
            {clearing ? "Clearing…" : "Clear Failures"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Accordion item for a single failed command.
 * Lazily fetches full command detail on expand.
 */
function FailureAccordion({
  command,
  expanded,
  onChange,
}: {
  command: any;
  expanded: boolean;
  onChange: () => void;
}) {
  const detail = useStableQuery(
    api.qbDesktopQueue.getCommandDetail,
    expanded ? { commandId: command._id } : "skip",
    null,
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={{
        background: "transparent",
        boxShadow: "none",
        "&::before": { display: "none" },
        border: "1px solid var(--c-overlay-6)",
        borderRadius: "8px !important",
        mb: 1,
        overflow: "hidden",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
          <Typography variant="body2">{command.commandType}</Typography>
          {command.statusCode && (
            <Chip label={command.statusCode} size="small" color="error" />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: "auto !important" }}>
            {relativeTime(command.createdAt)}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {detail ? (
          <Stack spacing={1}>
            {detail.errorMessage && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Error
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: 12,
                    color: "text.secondary",
                    background: "var(--c-overlay-2)",
                    borderRadius: 2,
                    p: 1.5,
                    m: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {detail.errorMessage}
                </Box>
              </Box>
            )}
            {detail.qbXml && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Request XML
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: 12,
                    color: "text.secondary",
                    background: "var(--c-overlay-2)",
                    borderRadius: 2,
                    p: 1.5,
                    m: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {detail.qbXml}
                </Box>
              </Box>
            )}
            {detail.responseXml && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Response XML
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontFamily: '"IBM Plex Mono", monospace',
                    fontSize: 12,
                    color: "text.secondary",
                    background: "var(--c-overlay-2)",
                    borderRadius: 2,
                    p: 1.5,
                    m: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {detail.responseXml}
                </Box>
              </Box>
            )}
            {!detail.errorMessage && !detail.qbXml && !detail.responseXml && (
              <Typography variant="body2" color="text.secondary">No detail available</Typography>
            )}
          </Stack>
        ) : (
          <LinearProgress />
        )}
      </AccordionDetails>
    </Accordion>
  );
}
